/* eslint-disable camelcase */
import moment from 'moment'
import {
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  IconButton,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosError, AxiosResponse } from 'axios'
import { plainToClass } from 'class-transformer'
import { isEmpty } from 'lodash'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { createRef, ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ASN, Batch, Client, FreightOrder, OrderStatusEnum, FreightOrderUnit, Pallet, Sku } from 'src/api/models'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'
import { Paginator, useQueryPaginator } from 'src/components/Table'
import { Notification } from 'src/components/Notification'
import {
  useForm,
  AddressField,
  BooleanField,
  DateField,
  GenerateChipDisplay,
  MapSelectField,
  NumberField,
  QueryMultiSelectField,
  QuerySelectField,
  TextareaField,
  TextField,
} from 'src/forms'
import { BatchDialog } from '../BatchDialog'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage } from 'src/components/Notification/utils'
import styled from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
import * as yup from 'yup'
import { FlagDialog } from './FlagDialog'
import { useMutation } from 'react-query'

const Centered = styled.div`
  text-align: center;
`

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface CustomFieldProps {
  name: string
  value: string
}

interface PalletProps {
  pallet: string
}

interface SkuProps {
  sku: string
  batch: string
  num_units: number // eslint-disable-line camelcase
}

interface AddressProps {
  company?: string
  consignee: string
  phone?: string
  address1: string
  address2?: string
  address3?: string
  city: string
  state: string
  postal_code: string // eslint-disable-line camelcase
}

interface OrderProps {
  client?: string
  number: string
  po?: string
  bol?: string
  details: string
  fulfill_on: string
  process_on: string
  carrier?: string
  retailer_routing_number: string
  status: string
  address: AddressProps
  custom_fields: CustomFieldProps[] // eslint-disable-line camelcase
  inventory: SkuProps[]
  pallets: PalletProps[]
  attachments?: AttachmentProp[]
}

const orderSchema = yup.object().shape({
  client: yup.string().label('Client'),
  number: yup
    .string()
    .nullable()
    .label('Order Number')
    .transform((value) => (value === '' ? undefined : value)),
  po: yup.string().optional().label('PO#'),
  bol: yup.string().optional().label('Bill of Lading'),
  fulfill_on: yup.date().optional().nullable().default(null),
  process_on: yup.date().optional().nullable().default(null),
  carrier: yup.string().label('Carrier').optional(),
  retailer_routing_number: yup.string().label('Retailer Routing Number').optional(),
  details: yup
    .string()
    .nullable()
    .label('Order Details')
    .transform((value) => (value === '' ? undefined : value)),
  address: yup
    .object({
      company: yup.string().label('Company').optional(),
      consignee: yup.string().label('Consignee').required(),
      phone: yup.string().label('Phone Number').optional().nullable(),
      address1: yup.string().label('Address 1').required(),
      address2: yup.string().label('Address 2'),
      address3: yup.string().label('Address 3'),
      city: yup.string().label('City').required(),
      state: yup.string().label('State').required(),
      postal_code: yup.string().label('Postal Code').required(),
    })
    .required(),
  status: yup.string().label('Status').required(),
  flags: yup.array().of(yup.string()),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
  inventory: yup.array().of(
    yup
      .object({
        sku: yup.string().required().label('SKU'),
        batch: yup.string().label('Batch'),
        num_units: yup
          .number()
          .required()
          .label('Number of Units')
          .transform((currentValue, originalValue) => {
            if (currentValue === '' || originalValue === '') {
              return undefined
            }
            return currentValue
          }),
      })
      .label('Inventory')
  ),
  pallets: yup.array().of(
    yup
      .object({
        pallet: yup.string().required().label('Pallet'),
      })
      .label('Pallets')
  ),
})

const bulkOrderSchema = yup.object().shape({
  fulfill_on: yup.date().optional().nullable(),
  details: yup
    .string()
    .nullable()
    .optional()
    .label('Order Details')
    .transform((value) => (value === '' ? undefined : value)),
  address: yup
    .object({
      company: yup.string().label('Company').optional(),
      consignee: yup.string().label('Consignee').optional(),
      phone: yup.string().label('Phone').optional().nullable(),
      address1: yup.string().label('Address 1').optional(),
      address2: yup.string().label('Address 2').optional(),
      address3: yup.string().label('Address 3').optional(),
      city: yup.string().label('City').optional(),
      state: yup.string().label('State').optional(),
      postal_code: yup.string().label('Postal Code').optional(),
    })
    .optional(),
  status: yup.string().label('Status').optional(),
  flags: yup.array().of(yup.string()),
})

interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

export interface FlagDialogValues {
  name?: string
  updateValues?: (value: any) => void
}

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  order?: FreightOrder
  asn?: ASN
  bulkUpdate?: boolean
  orders?: FreightOrder[]
}

interface InventoryListRowProps {
  value: any
  skuPaginator: Paginator
  client: Client | null
  listItemName: string
  isDisabled: boolean
  onRemove: (arg0: string) => void
  bulkUpdate: boolean
}
const InventoryListRow = (props: InventoryListRowProps) => {
  const { skuPaginator, listItemName, isDisabled, client, onRemove } = props
  const batchPaginator = useQueryPaginator(
    'batch-list',
    skuit.sc.batches.list,
    {
      params: {},
    },
    { enabled: !props.bulkUpdate }
  )
  const patchBatchFilters = batchPaginator.patchFilters
  useEffect(() => {
      patchBatchFilters({ clients: client?.guid })
  }, [
    client,
    patchBatchFilters,
  ])
  return <Grid container spacing={2} key={listItemName}>
    <Grid item xs={4}>
      <QuerySelectField
        name="sku"
        label="[SKU] Product"
        paginator={skuPaginator}
        getOptionLabel={(sku) => `|${sku?.sku}| ${sku?.product?.name}`}
        renderOption={(option) => {
          return (
            <Grid container spacing={2} m={2}>
              <Grid item xs={12}>
                <Typography>SKU: {option?.sku}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>Product: {option?.product?.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  Number Available: {option?.numAvailable}
                </Typography>
              </Grid>
            </Grid>
          )
        }
        }
      />
    </Grid>
    <Grid item xs={3}>
      <QuerySelectField
        name="batch"
        label="Batch"
        paginator={batchPaginator}
        disabled={!client?.guid || isDisabled}
        CreateDialog={(props) => {
          return <BatchDialog {...props} batch={client ? {client} : undefined} allowSelectASN={false}/>
        }}
        createOnSave={(_) => {
          batchPaginator.refresh()
        }}
      />
    </Grid>
    <Grid item xs={2}>
      <NumberField
        name="num_units"
        label="Number of Units"
        disabled={!client?.guid || isDisabled}
      />
    </Grid>
    <Grid item xs={2}>
      <BooleanField
        name="backorder"
        label="Backorder"
      />
    </Grid>
    <Grid item xs={1}>
      <Button
        disabled={isDisabled}
        onClick={()=>onRemove(listItemName)}
      >
        <CloseIcon /> Delete
      </Button>
    </Grid>
  </Grid>
}

export const FreightOrderDialog = (props: Props) => {
  const { open, handleClose, onSave, update, order, asn, bulkUpdate = false, orders = [] } = props

  const classes = useStyles()

  const [saving, setSaving] = useState(false)

  // if all the orders are the same client, set the client
  const [firstOrder, ...rest] = orders
  const ordersClient: Client | null = rest.reduce<Client | null>((value, obj) => {
    return value?.guid === obj.client?.guid ? value : null
  }, firstOrder?.client)

  const [client, setClient] = useState<Client | null>(
    order?.client || asn?.client || ordersClient
  )
  const [inventoryOpen, setInventoryOpen] = useState(true)
  const [palletsOpen, setPalletsOpen] = useState(true)

  const clientPaginator = useQueryPaginator('client-list', skuit.sc.clients.list, {
    params: { search: order?.client?.name || asn?.client?.name },
  })
  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const carrierPaginator = useQueryPaginator('carrier-list', skuit.sc.carriers.list)
  const skuPaginator = useQueryPaginator(
    'sku-list',
    skuit.sc.skus.list,
    {
      params: { active: 'true' },
    },
    { enabled: !bulkUpdate }
  )
  const palletPaginator = useQueryPaginator(
    'pallet-list',
    skuit.sc.pallets.list,
    {
      params: { stored: 'true', available: 'true' },
    },
    { enabled: !bulkUpdate }
  )
  const customFieldPaginator = useQueryPaginator(
    'custom-fields',
    skuit.sc.freight.orders.customFields.list,
    {
      params: { clients: order?.client?.guid || 'ac_noac' },
    }
  )
  const customFieldValues: Record<string, string> = {}
  if (order?.customFields?.length) {
    for (const field of order.customFields) {
      customFieldValues[field.name || ''] = field.value || ''
    }
  }

  const orderUnitPaginator = useQueryPaginator(
    'order-unit-list',
    skuit.sc.freight.orders.units.list,
    {
      params: { orders: order?.guid || 'or_noorder', 'on-container': 'false' },
    },
    { enabled: !bulkUpdate }
  )
  const orderPalletPaginator = useQueryPaginator(
    'order-pallet-list',
    skuit.sc.freight.orders.pallets.list,
    { params: { orders: order?.guid || 'or_noorder' } },
    { enabled: !bulkUpdate }
  )

  const asnPalletPaginator = useQueryPaginator(
    'pallet-list',
    skuit.sc.pallets.list,
    {
      params: { asns: asn?.guid || 'as_noasn' },
    },
    { enabled: !bulkUpdate }
  )

  const tagPaginator = useQueryPaginator(
    'tag-list',
    skuit.sc.attachments.tags.list,
    {},
    { enabled: !bulkUpdate }
  )
  const flagPaginator = useQueryPaginator('flag-list', skuit.sc.freight.orders.flags.list)

  const {
    Form,
    Field,
    FieldList,
    data: formData,
  } = useForm(bulkUpdate ? bulkOrderSchema : orderSchema)

  const deGetPallets = useDebouncedCallback(palletPaginator.patchFilters, 100)

  const patchSkuFilters = skuPaginator.patchFilters
  const patchPalletFilters = palletPaginator.patchFilters
  const patchCustomFieldFilters = customFieldPaginator.patchFilters
  const setFormData = formData.setValue
  useEffect(() => {
    if (client?.guid) {
      patchSkuFilters({ clients: client.guid })
      patchPalletFilters({ clients: client.guid })
      patchCustomFieldFilters({ clients: client.guid })
      if (!order && !asn && !bulkUpdate) {
        setFormData('inventory', [])
        setFormData('pallets', [])
      }
    }
  }, [
    asn,
    order,
    client,
    patchSkuFilters,
    patchPalletFilters,
    setFormData,
    patchCustomFieldFilters,
    bulkUpdate,
  ])

  const skuFileInput = createRef<HTMLInputElement>()

  const { mutate: submitSkus } = useMutation(skuit.sc.freight.orders.skuAvailability, {
    onSuccess: (response: AxiosResponse) => {
      const newInventory = response.data.map(
        (field: { sku: Sku; batch: Batch; num_units: number; lookup: string }, index: number) => {
          // eslint-disable-line camelcase
          const { sku, batch, num_units, lookup } = field // eslint-disable-line camelcase
          const skuObj = plainToClass(Sku, sku)
          const existing = formData.getValue('inventory')
          if (!skuObj) {
            formData.setError(
              `inventory[${index + existing.length}].sku`,
              `Invalid SKU lookup: ${lookup}`
            )
          } else if (num_units > (skuObj.numAvailable || 0)) {
            formData.setError(
              `inventory[${index + existing.length}].num_units`,
              'Not enough available units'
            )
          }
          return { sku: skuObj, batch, num_units }
        }
      )
      formData.extend('inventory', newInventory)
    },
    onError: (error: AxiosError) => {
      if (skuFileInput.current) {
        skuFileInput.current.value = ''
      }
      if (error?.response?.data?.details) {
        toast.error(() => (
          <Notification message={`Failed to process CSV: ${error?.response?.data.details}`} />
        ))
      } else {
        toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
      }
    },
  })

  const { mutate: submitPallets } = useMutation(skuit.sc.freight.orders.palletAvailability, {
    onSuccess: (response: AxiosResponse) => {
      const newPallets = response.data.map(
        (field: { pallet: Pallet; lookup: string }, index: number) => {
          // eslint-disable-line camelcase
          const { pallet, lookup } = field // eslint-disable-line camelcase
          const palletObj = plainToClass(Pallet, pallet)
          const existing = formData.getValue('inventory')
          if (!palletObj) {
            formData.setError(
              `pallets[${index + existing.length}].sku`,
              `Invalid Pallet lookup: ${lookup}`
            )
          }
          return { pallet: palletObj }
        }
      )
      formData.extend('pallets', newPallets)
    },
    onError: (error: AxiosError) => {
      toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  const { mutate: bulkUpdateOrders } = useMutation(skuit.sc.freight.orders.bulkUpdate, {
    onSuccess: () => {
      orderUnitPaginator.refresh()
      orderPalletPaginator.refresh()
      handleClose()
      if (onSave) {
        onSave()
      }
      toast.success(() => <Notification message={`Successfully updated ${orders.length} orders`} />)
      setSaving(false)
    },
    onError: (error: AxiosError) => {
      toast.error(() => <Notification message={`Failed to update orders: ${error}`} />)
      setSaving(false)
    },
  })

  const onSkuFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const form = new FormData()
      form.append('file', e.target.files[0])
      let params: any
      if (order?.client?.guid) {
        params = { client: order.client.guid }
      }
      submitSkus(form, params)
    }
  }

  const palletFileInput = createRef<HTMLInputElement>()
  const onPalletFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const form = new FormData()
      form.append('file', e.target.files[0])
      submitPallets(form)
    }
  }

  const startingInventory = orderUnitPaginator.data.count
    ? orderUnitPaginator.data.results.map((item: FreightOrderUnit) => ({
        sku: item.sku,
        batch: item.batch,
        num_units: item.numUnits,
      }))
    : undefined
  const startingPallets: { pallet: Pallet }[] = []
  if (orderPalletPaginator.data.count) {
    for (const item of orderPalletPaginator.data.results) {
      startingPallets.push({ pallet: item.pallet })
    }
  }
  if (asnPalletPaginator.data.count) {
    for (const item of asnPalletPaginator.data.results) {
      startingPallets.push({ pallet: item })
    }
  }
  const defaultPallets = startingPallets.length ? startingPallets : undefined

  const onSubmit = (data: OrderProps) => {
    setSaving(true)
    const attachments = data.attachments
    delete data.attachments

    const customFields = []
    for (const field of customFieldPaginator.data.results) {
      if (field.name in data) {
        customFields.push({ name: field.name, value: (data as any)[field.name] })
        delete (data as any)[field.name]
      }
    }
    if (customFields.length) {
      data.custom_fields = customFields
    }

    if (data.process_on) {
      const processOn = moment(data.process_on)
      data.process_on = processOn.format('YYYY-MM-DD')
    }

    if (data.fulfill_on) {
      const fulfillOn = moment(data.fulfill_on)
      data.fulfill_on = fulfillOn.format('YYYY-MM-DD')
    }

    if (bulkUpdate) {
      const bulkData = {
        guids: orders.map((order) => order.guid),
        detail: data,
      }
      bulkUpdateOrders(bulkData)
      return
    }

    if (!data.inventory) {
      data.inventory = []
    }
    if (!data.pallets) {
      data.pallets = []
    }

    let func = skuit.sc.freight.orders.create
    let action = 'creat'
    if (update) {
      func = (data: OrderProps) => skuit.sc.freight.orders.patch(order?.guid || '', data)
      action = 'updat'
      delete data.client
    }
    func(data)
      .then((newOrder: FreightOrder) => {
        if (attachments?.length) {
          skuit.sc.freight.orders.attachments
            .create(newOrder.guid as string, { attachments })
            .then(() => {
              orderUnitPaginator.refresh()
              orderPalletPaginator.refresh()
              handleClose()
              if (onSave) {
                onSave()
              }
              toast.success(() => (
                <Notification
                  message={`Successfully ${action}ed Order: ${newOrder.name}`}
                  newObject={newOrder}
                />
              ))
              toast.success(() => (
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              ))
            })
            .catch((attachmentError: AxiosError) => {
              handleErrorMessage(attachmentError, action, 'Attachments')
              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          orderUnitPaginator.refresh()
          orderPalletPaginator.refresh()
          handleClose()
          if (onSave) {
            onSave()
          }
          toast.success(() => (
            <Notification
              message={`Successfully ${action}ed Order: ${newOrder.name}`}
              newObject={newOrder}
            />
          ))
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Order')
        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }
  let saveLabel: string
  if (orderUnitPaginator.loading || orderPalletPaginator.loading) {
    saveLabel = 'Loading...'
  } else if (saving) {
    saveLabel = 'Saving...'
  } else {
    saveLabel = 'Save'
  }

  const isInventoryDisabled =
    order?.status === 'processed' ||
    order?.status === 'shipped' ||
    order?.status === 'delivered' ||
    order?.status === 'canceled'

  const isOnlyStatusChanges =
    order?.status === 'shipped' || order?.status === 'delivered' || order?.status === 'canceled'

  const title = bulkUpdate ? 'Bulk Update' : update ? 'Update' : 'Create'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{title} Order</DialogTitle>
        <DialogContent>
          {!bulkUpdate && (
            <QuerySelectField
              name="client"
              label="Client"
              paginator={clientPaginator}
              defaultValue={order?.client || asn?.client}
              disabled={update || !!asn || isOnlyStatusChanges}
              onSelected={(client) => setClient(client)}
            />
          )}
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={order?.facility || asn?.facility}
            disabled={isOnlyStatusChanges}
          />
          {!bulkUpdate && (
            <TextField
              name="number"
              label="Order Number"
              defaultValue={order?.number}
              disabled={isOnlyStatusChanges}
            />
          )}
          {!bulkUpdate && (
            <TextField
              name="po"
              label="Purchase Order Number"
              defaultValue={order?.po}
              disabled={isOnlyStatusChanges}
            />
          )}
          <QuerySelectField
            name="carrier"
            label="Carrier"
            paginator={carrierPaginator}
            defaultValue={order ? order.carrier : undefined}
            disabled={isOnlyStatusChanges}
            getOptionLabel={(option) => `${option.name}${option.scac ? ' [' + option.scac + ']' : ''}`}
            renderOption={(option) => (
              <span>
                {option.name}
                {option.scac && ` [${option.scac}]`}
              </span>
            )}
          />
          {!bulkUpdate && (
            <TextField
              name="retailer_routing_number"
              label="Retailer Routing Number"
              defaultValue={order?.retailerRoutingNumber}
              disabled={isOnlyStatusChanges}
            />
          )}
          <DateField
            name="process_on"
            label="Date to Process On"
            defaultValue={order?.processOn}
            disabled={isOnlyStatusChanges}
          />
          <DateField
            name="fulfill_on"
            label="Date to Fulfill On"
            defaultValue={order?.fulfillOn}
            disabled={isOnlyStatusChanges}
          />
          <TextareaField
            name="details"
            label="Order Details"
            defaultValue={order?.details}
            disabled={isOnlyStatusChanges}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MapSelectField
                name="status"
                label="Status"
                options={FreightOrder.Status}
                allowNull={bulkUpdate}
                defaultValue={order?.status || (bulkUpdate ? undefined : OrderStatusEnum.received)}
              />
            </Grid>
            <Grid item xs={6}>
              <QueryMultiSelectField
                name="flags"
                label="Flags"
                paginator={flagPaginator}
                defaultValue={order?.flags}
                disabled={isOnlyStatusChanges}
                renderSelected={GenerateChipDisplay(['name'])}
                CreateDialog={FlagDialog}
              />
            </Grid>
          </Grid>
          <Typography variant="h6" align="center" mt={12}>
            Shipping
          </Typography>
          <AddressField
            name="address"
            defaultValue={order?.address}
          />
          {customFieldPaginator.data.results.map((field) => (
            <TextField
              key={field.name}
              name={field.name}
              label={field.label}
              defaultValue={customFieldValues[field.name]}
              disabled={isOnlyStatusChanges}
            />
          ))}
          {!bulkUpdate && (
            <>
              <Typography variant="h6" mt={6} mb={3}>
                Attachments
              </Typography>
              <FieldList
                name="attachments"
                startCount={0}
                renderHeader={({ name, value, setValue }) =>
                  order?.status === 'canceled' ? null : (
                    <DropzoneAreaBase
                      dropzoneText="Drag and drop or click to add files"
                      fileObjects={[]}
                      onAdd={(files) => {
                        const fileData = files.map((fileObj) => {
                          return { file: fileObj.file, tags: [] }
                        })
                        const newValue = (value || []).concat(fileData)
                        setValue(name, newValue)
                      }}
                      filesLimit={10}
                      maxFileSize={75000000}
                      showPreviewsInDropzone={false}
                    />
                  )
                }
                renderList={ImageListWrapper}
                renderField={({ name: itemName }) => (
                  <Grid item xs={3} key={itemName}>
                    <Card>
                      <CardContent>
                        <Field
                          name="file"
                          renderField={({ value, remove, error }) => {
                            if (!value) {
                              return null
                            }
                            return (
                              <>
                                <FilePreview file={value} onDelete={() => remove(itemName)} />
                                {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                              </>
                            )
                          }}
                        />
                        <Field
                          name="url"
                          renderField={({ value, remove, error }) => {
                            if (!value) {
                              return null
                            }
                            return (
                              <>
                                <UrlPreview
                                  url={value}
                                  allowDownload={false}
                                  onDelete={() => remove(itemName)}
                                />
                                {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                              </>
                            )
                          }}
                        />
                        <Field
                          name="tags"
                          getSubmitValue={(value) =>
                            value && value.map((tag: { slug: string }) => tag.slug)
                          }
                          renderField={({ name, value, setValue, error }) => (
                            <Autocomplete
                              disabled={isOnlyStatusChanges}
                              id={name}
                              freeSolo
                              options={tagPaginator.data.results}
                              getOptionLabel={(tag) => tag.name}
                              getOptionSelected={(option, value) => option.guid === value?.guid}
                              onChange={(_: any, newValue: any | null) => setValue(name, newValue)}
                              value={value}
                              renderInput={(params) => (
                                <MuiTextField
                                  {...params}
                                  error={!!error}
                                  helperText={error}
                                  label="Tags"
                                />
                              )}
                            />
                          )}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              />

              <Typography variant="h6" mt={12}>
                Inventory{' '}
                <IconButton onClick={() => setInventoryOpen(!inventoryOpen)}>
                  {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Typography>
              <Collapse in={inventoryOpen}>
                {orderUnitPaginator.loading ? (
                  <CircularProgress color="inherit" />
                ) : (
                  <FieldList
                    name="inventory"
                    startCount={0}
                    defaultValues={startingInventory}
                    renderField={({ name: listItemName, data, value }) => {
                      return <InventoryListRow
                        value={value}
                        skuPaginator={skuPaginator}
                        listItemName={listItemName}
                        client={client || null}
                        onRemove={(listItemName)=> {data.remove(listItemName)}}
                        isDisabled={isInventoryDisabled}
                        bulkUpdate={bulkUpdate}/>
                      }}
                  />
                )}
                <Centered>
                  <Grid container justify="center" spacing={6}>
                    <Grid item>
                      <Button
                        disabled={!client?.guid || isInventoryDisabled}
                        variant="contained"
                        color="primary"
                        mt={6}
                        onClick={() =>
                          formData.append('inventory', { sku: undefined, num_units: undefined })
                        }
                      >
                        <AddIcon />
                        Add SKUs
                      </Button>
                    </Grid>
                    <Grid item>
                      <input
                        type="file"
                        accept=".csv"
                        ref={skuFileInput}
                        style={{ display: 'none' }}
                        onChange={onSkuFileSelected}
                      />
                      <Button
                        disabled={!client?.guid || isInventoryDisabled}
                        variant="contained"
                        color="primary"
                        mt={6}
                        onClick={() => {
                          if (skuFileInput.current) {
                            skuFileInput.current.click()
                          }
                        }}
                      >
                        <AddIcon />
                        Add SKUs by CSV
                      </Button>
                    </Grid>
                  </Grid>
                </Centered>
              </Collapse>
              <Typography variant="h6" mt={12}>
                Pallets{' '}
                <IconButton onClick={() => setPalletsOpen(!palletsOpen)}>
                  {palletsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Typography>
              <Collapse in={palletsOpen}>
                {orderPalletPaginator.loading || asnPalletPaginator.loading ? (
                  <CircularProgress color="inherit" />
                ) : (
                  <FieldList
                    name="pallets"
                    startCount={0}
                    defaultValues={defaultPallets}
                    renderField={({ name: listItemName, data }) => (
                      <Grid container spacing={2} key={listItemName}>
                        <Grid item xs={10}>
                          <Field
                            name="pallet"
                            getSubmitValue={(pallet) => pallet?.guid}
                            renderField={({ name, value, setValue, error }) => (
                              <Autocomplete
                                disabled={!client?.guid || isInventoryDisabled}
                                options={palletPaginator.data.results}
                                getOptionLabel={(option) => option.name}
                                getOptionSelected={(option, value) => option.guid === value?.guid}
                                renderOption={(option) => {
                                  return (
                                    <Grid container spacing={2} m={2}>
                                      <Grid item xs={12}>
                                        <Box
                                          display="flex"
                                          flexDirection="row"
                                          alignItems="baseline"
                                          justifyContent="space-between"
                                          style={{ width: '100%' }}
                                        >
                                          <Typography>Name: {option?.name}</Typography>
                                          <Typography>
                                            Your ID: {option?.clientID || '--'}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box
                                          display="flex"
                                          flexDirection="row"
                                          alignItems="baseline"
                                          justifyContent="space-between"
                                          style={{ width: '100%' }}
                                        >
                                          <Typography>SKUs: {option?.skuStr || '--'}</Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="h6">ASN</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box
                                          display="flex"
                                          flexDirection="row"
                                          alignItems="baseline"
                                          justifyContent="space-between"
                                          style={{ width: '100%' }}
                                        >
                                          <Typography>
                                            Tracking Number: {option?.asn?.trackingNumber || '--'}
                                          </Typography>
                                          <Typography>
                                            Bill of Lading Number:{' '}
                                            {option?.asn?.billOfLadingNumber || '--'}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      {option?.asn?.customFields &&
                                        Object.keys(option.asn.customFields).map((label) => (
                                          <Grid item xs={12} key={label}>
                                            <Typography>
                                              {label}: {option.asn.customFields[label] || '--'}
                                            </Typography>
                                          </Grid>
                                        ))}
                                    </Grid>
                                  )
                                }}
                                value={value}
                                filterOptions={(option) => option}
                                onChange={(_, value) => {
                                  setValue(name, value, () => {
                                    const exclude = formData.values.pallets
                                      .map(({ pallet }: { pallet: Pallet }) => pallet.guid)
                                      .join(',')
                                    deGetPallets.callback({ exclude })
                                  })
                                }}
                                renderInput={(params) => (
                                  <MuiTextField
                                    {...params}
                                    name={name}
                                    label="Pallet"
                                    error={!isEmpty(error)}
                                    helperText={error}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                      deGetPallets.callback({ search: event.target.value })
                                    }
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Button
                            disabled={isInventoryDisabled}
                            onClick={() => {
                              data.remove(listItemName)
                            }}
                          >
                            <CloseIcon /> Delete
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  />
                )}
                <Centered>
                  <Grid container justify="center" spacing={6}>
                    <Grid item>
                      <Button
                        disabled={!client?.guid || isInventoryDisabled}
                        variant="contained"
                        color="primary"
                        mt={6}
                        onClick={() => formData.append('pallets', { pallet: undefined })}
                      >
                        <AddIcon />
                        Add Pallets
                      </Button>
                    </Grid>
                    <Grid item>
                      <input
                        type="file"
                        accept=".csv"
                        ref={palletFileInput}
                        style={{ display: 'none' }}
                        onChange={onPalletFileSelected}
                      />
                      <Button
                        disabled={!client?.guid || isInventoryDisabled}
                        variant="contained"
                        color="primary"
                        mt={6}
                        onClick={() => {
                          if (palletFileInput.current) {
                            palletFileInput.current.click()
                          }
                        }}
                      >
                        <AddIcon />
                        Add Pallets by CSV
                      </Button>
                    </Grid>
                  </Grid>
                </Centered>
              </Collapse>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={
              saving ||
              orderUnitPaginator.loading ||
              orderPalletPaginator.loading ||
              asnPalletPaginator.loading
            }
            autoFocus
          >
            {saveLabel}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
